import React, {useEffect, useState} from "react";
import {createPortal} from "react-dom";

const ImageLightbox = ({src, fallbackSrc, alt = "Full size image", onClose}) => {
    const [displaySrc, setDisplaySrc] = useState(src);
    const isVideo = /\.mp4(\?|$)/i.test(src || "") || String(src || "").includes("/vse-vms-transcoding-artifact-");

    useEffect(() => {
        setDisplaySrc(src);
    }, [src]);

    useEffect(() => {
        if (!src) return undefined;

        const handleKeyDown = (event) => {
            if (event.key === "Escape") onClose?.();
        };

        document.addEventListener("keydown", handleKeyDown);
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            document.body.style.overflow = previousOverflow;
        };
    }, [src, onClose]);

    if (!src || typeof document === "undefined") return null;

    return createPortal(
        <div
            className="vl-image-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label={alt}
            onClick={onClose}
        >
            <style>{`
                .vl-image-lightbox {
                    position: fixed;
                    inset: 0;
                    z-index: 3000;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 100vw;
                    height: 100vh;
                    padding: 72px 28px 32px;
                    background:
                        radial-gradient(circle at center, rgba(18, 38, 31, 0.78), rgba(0, 0, 0, 0.92));
                    backdrop-filter: blur(5px);
                    overflow: hidden;
                }
                .vl-image-lightbox__image {
                    display: block;
                    max-width: calc(100vw - 56px);
                    max-height: calc(100vh - 104px);
                    object-fit: contain;
                    border-radius: 10px;
                    box-shadow: 0 24px 80px rgba(0, 0, 0, 0.55);
                }
                .vl-image-lightbox__close {
                    position: fixed;
                    top: 18px;
                    right: 22px;
                    width: 42px;
                    height: 42px;
                    border: 1px solid rgba(255,255,255,0.32);
                    border-radius: 999px;
                    background: rgba(10, 20, 16, 0.52);
                    color: #fff;
                    font-size: 28px;
                    line-height: 1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: 0 10px 28px rgba(0, 0, 0, 0.28);
                }
                .vl-image-lightbox__close:hover {
                    background: rgba(25, 135, 84, 0.82);
                }
            `}</style>
            <button
                type="button"
                className="vl-image-lightbox__close"
                aria-label="Close full size image"
                onClick={(event) => {
                    event.stopPropagation();
                    onClose?.();
                }}
            >
                &times;
            </button>
            {isVideo ? (
                <video
                    src={src}
                    controls
                    autoPlay
                    className="vl-image-lightbox__image"
                    onClick={(event) => event.stopPropagation()}
                />
            ) : (
                <img
                    src={displaySrc || src}
                    alt={alt}
                    className="vl-image-lightbox__image"
                    onError={() => {
                        if (fallbackSrc && displaySrc !== fallbackSrc) {
                            setDisplaySrc(fallbackSrc);
                        }
                    }}
                    onClick={(event) => event.stopPropagation()}
                />
            )}
        </div>,
        document.body
    );
};

export default ImageLightbox;
